import React, { useEffect, useState } from 'react';
import { useSearchParams, NavLink } from "react-router-dom";
import LoggedLayout from '../../layouts/LoggedLayout';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import PageHeader from 'components/PageHeader';
import Paginator from 'components/Paginator';
import ThSortable from 'components/ThSortable';
import TrSkeleton from 'components/TrSkeleton';
import DateDropdown from 'components/DateDropdown';
import Details from './Details';
import EmpoweredSelector from 'components/EmpoweredSelector';
import { default as SuppliesService } from 'services/Supplies';
import { default as CompaniesService } from 'services/Companies';

const Filters = styled.div`
	display: flex;
	justify-content: end;
	align-items: center;

	.filters-inputs {
	    display: flex;
		flex-direction: row;
		align-items: center;
		flex-wrap: nowrap;

		@media (max-width: 1200px) {
			& {
				display: flex !important;
				justify-content: flex-end;
				width: 100% !important;
			}
		}

		@media (min-width: 576px) and (max-width: 1200px) {
			& > *:nth-child(2) {
				min-width: 200px;
			}
		}

		@media (min-width: 768px) and (max-width: 991px) {
			#search-box {
				flex: 1;
			}
		}

		@media (max-width: 576px) {
			flex-direction: column;
			align-items: stretch;
			gap: 1rem;
			width: 100%;
			max-width: 100%;
			box-sizing: border-box;

			& > * {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}

			#search-box {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}
		}

		@media (max-width: 374px) {
			& {
				margin: auto;
			}
		}
	}

	.filter-buttons{
		width: auto !important;

		& > * {
			@media (min-width: 1201px) {
				height: 100%;
			}

			@media (max-width: 576px) {
				width: 100%;
				text-align: center;
			}
		}

		@media (max-width: 1200px) {
			margin-right: 0.5rem;
		}

		@media (max-width: 991px) {
			width: calc(100% - 1.5rem) !important;
		}

		@media (max-width: 767px) {
			width: calc(100% - 2rem);
		}

		@media (max-width: 576px) {
			display: flex !important;
			flex-direction: column !important;
			flex-wrap: nowrap;
			align-items: stretch !important;
			width: 100% !important;
		}
	}
	
	@media (max-width: 1200px) {
		justify-content: center;
		flex-direction: column;
		align-items: flex-end;

		& > *,
		.empowered-selector {
			width: 100% !important;
			display: block !important;
		}

		& > * {
			margin-bottom: 10px;
		}
	}

	@media (max-width: 991px) {
		display: grid;
		grid-template-columns: 1fr;
		grid-auto-rows: auto;
		gap: 10px;
		align-items: stretch;

		& > * {
			width: 100% !important;
			margin-bottom: 0 !important;
		}
	}

	@media (max-width: 767px) {
		grid-template-columns: 1fr;
	}
`;

const DateDropDownWrapper = styled.div` 
	padding: 0;
	min-height: 0;
	width: 192px;
	display: inline-block;

	& > .date-dropdown {
		display: block;

		& > .dropdown {
			width: 100%;
			display: block !important;

			& > .btn {
				padding: 0;
				width: 100% !important;
				display: flex;
				align-items: center;
				padding: 0 5px;

				small {
					margin-left: auto;
				}
			}
		}
	}
`;

const EmpoweredSelectorWrapper = styled.div` 
	margin-right: 10px;

	.empowered-selector {
		width: 200px;
		
		.empowered-selector_label {
			height: 28px;
			white-space: nowrap;
			overflow: hidden;

			&:hover {
				background: white;
			}

			.remove-icon {
				border-radius: 50%;
			}
		}
	}
`;

const HeaderCompanyEmpoweredSelector = styled.div`
	position: relative;
	padding-left: 10px;
	padding-right: 10px;
	width: 200px;
	border-right: 1px solid var(--bs-gray-500);
	
	.empowered-selector {

		.empowered-selector_label {
			border: 0;
			background: white;
		}
	}
`;

const HeaderButtonsTab = styled.div` 
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 100%;

	a {
		display: flex;
		text-decoration: none;
		padding: 0px 10px;
		width: 50%;
		height: 100%;
		align-items: center;
		justify-content: center;
		text-transform: uppercase;

		&.active {
			background: var(--plenoil-blue);
			color: white;
		}
	}
`;

let axiosCancelToken = null;

export default function Supplies(props) {
	const type = props.type;

	const [queryParams] = useSearchParams();

	let [forceReload, setForceReload] = useState(null);
	let [orders, setOrders] = useState([]);
	let [sortDirection, setSortDirection] = useState('desc');
	let [sortField, setSortField] = useState('datetime');
	let [detailsOpened, setDetailsOpened] = useState([]);
	let [details, setDetails] = useState({});
	let [station, setStation] = useState(null);
	let [company, setCompany] = useState(null);
	let [dateFrom, setDateFrom] = useState(moment().format('YYYY-MM-DD'));
	let [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));
	let [skeletonRows, setSkeletonRows] = useState(5);
	let [page, _setPage] = useState(queryParams.get('page') ?? undefined);
	const setPage = (page) => {
		setOrders({...orders, data: undefined});
		_setPage(page);
	}

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};	

	const toggleDetails = (rowId) => {
		if ( detailsOpened.includes(rowId) ) {
			detailsOpened.splice(detailsOpened.indexOf(rowId), 1);
			setDetailsOpened([...detailsOpened]);

			// Remove details
			delete details[rowId];
			setDetails({...details});
		} else {
			setDetailsOpened([...detailsOpened, rowId]);
		}
	}

	const setDatesRange = (range) => {
		setDateFrom(range.from);
		setDateTo(range.to);
	}

	const loadStations = (input, callback, args) => {
		// TODO from service
		axios.get('/api/stations/list', {
			params: {
				search: input,
				no_paginate: true,
				company_code: args.company_code
			},
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let formatted = response.data.map((el) => {
				return {
					value: el, 
					label: el.name
				};
			});

			callback(formatted);
	  	}).catch((error) => {			
			if ( axios.isCancel(error) ) return;
		});	
	}

	const loadCompanies = async (input, callback) => {
		let companies = await CompaniesService.list({no_paginate: true, search: input});

		let formatted = companies.map((el) => {
			return {
				value: el, 
				label: el.name
			};
		});

		callback(formatted);
	}

	const openChat = (e, station_id) => {
		e.stopPropagation();

		const event = new CustomEvent('open-chat', {detail: {stationId: station_id, channel: 'supplies'}});
		document.dispatchEvent(event);
	}

	useEffect(() => {
		axiosCancelToken = axios.CancelToken.source();

		return function cleanup() {
           	axiosCancelToken.cancel();
        }
	}, []);

	useEffect(() => {
		let getOrders = async () => {
			let orders = await SuppliesService.getBCOrdersTc({
				page: page,
				sort: sortField,
				direction: sortDirection,
				date_from: dateFrom,
				date_to: dateTo,
				station_id: station?.id,
				company_id: company?.id,
				type: type
			});
			setSkeletonRows(orders?.data?.length);
			setOrders({...orders});
		}
		getOrders();

		let reloadInterval = setInterval(() => {
			getOrders();
		}, 5000);

		return function cleanup() {
			clearInterval(reloadInterval);
		}
	}, [forceReload, page, sortField, sortDirection, station, company, dateFrom, dateTo, type]);

	return (
		<LoggedLayout>
			<div className="col-md-12 mb-3">
				<PageHeader style={{overflowX: 'visible', overflowY: 'visible'}}>
					<span className="header-column header-title">Suministros Comprobaciones</span>
					<HeaderCompanyEmpoweredSelector>
						<EmpoweredSelector
							load={(input, callback) => loadCompanies(input, callback)}
							onChange={(value) => setCompany(value)}
							timeout={200}
							label={company?.name}
							value={company?.id ?? null}
							placeholder="EMPRESA"
							noRemovable={false}
							noSearcheable={true}
						/>
					</HeaderCompanyEmpoweredSelector>
					<span className="header-column header-icon-end">
						<i className="bi bi-printer text-plenoil-gray"></i>
					</span>
				</PageHeader>
			</div>

			<div className="col-md-12 mb-3">
				<div className="bg-white p-2 rounded shadow-sm">
					<div className="row">
						<div className="col-lg-2 align-content-center">
							<HeaderButtonsTab>
								<NavLink to="/supplies/bc-orderstc/station" className="btn btn-sm btn-plenoil-outline-orange btn-plenoil p-0 px-2 me-2 text-nowrap col-4 col-lg-auto">estaciones</NavLink>
								<NavLink to="/supplies/bc-orderstc/wash" className="btn btn-sm btn-plenoil-outline-orange btn-plenoil p-0 px-2 me-2 text-nowrap col-4 col-lg-auto">lavaderos</NavLink>
							</HeaderButtonsTab>
						</div>
						<Filters className="col-lg-10">
							<div className="filters-inputs">
								<EmpoweredSelectorWrapper>
									<EmpoweredSelector
										load={(input, callback, args) => loadStations(input, callback, args)}
										args={{company_code: company?.code}}
										onChange={(value, text) => setStation(value)}
										timeout={200}
										label={station?.name}
										value={station?.id ?? null}
										placeholder="Estación"
									/>
								</EmpoweredSelectorWrapper>

								<DateDropDownWrapper className="form-control form-control_flotas control-input me-2 h-100">
									<DateDropdown 
										defaultPeriod={'today'} 
										onChange={(period, range) => setDatesRange(range)}
										onlyDates={true}
									/>
								</DateDropDownWrapper>
							</div>
						</Filters>
					</div>
				</div>
			</div>

			<div className="pt-2 col-md-12">
				<div className="px-0 col-lg pl-lg-0">
					<div className="row m-0 pb-2">
						<div className="col-12 px-0">
							<div className="h-100 p-4 background-color-blanco shadow rounded">
								<div className="table-responsive-rows">
									<div className="mvc-grid mvc-grid-header-mode">
										<div className="mvc-grid-table">
											<table>
												<thead>
													<tr>
														<ThSortable className="title_supplies-estacion" direction={sortDirection} active={sortField === 'station_id'} onClick={() => sortTableClick('station_id')}>Estación</ThSortable>
														<ThSortable className="title_supplies-fecha" direction={sortDirection} active={sortField === 'datetime'} onClick={() => sortTableClick('datetime')}>Fecha</ThSortable>
														<ThSortable className="title_supplies-fecha-envio" direction={sortDirection} active={sortField === 'sent_at'} onClick={() => sortTableClick('sent_at')}>Fecha envío</ThSortable>
														<ThSortable className="title_supplies-pedido" direction={sortDirection} active={sortField === 'code'} onClick={() => sortTableClick('code')}>Pedido Nº</ThSortable>
														<th className="title_supplies-albaran">Albaran</th>
														<ThSortable className="title_supplies-estado" direction={sortDirection} active={sortField === 'status'} onClick={() => sortTableClick('status')}>Estado</ThSortable>
														<th className="title_supplies-chat">Chat</th>
													</tr>
												</thead>
												<tbody>
													{ orders.data?.map((el, idx) => {
														let statusClass = '';
														let statusText = '';

														switch (el.status) {
															case 'unapproved':
																statusClass = 'bg-secondary';
																statusText = 'No aprobado';
																break;
															case 'pending':
																statusClass = 'bg-warning text-dark';
																statusText = 'Pendiente';
																break;
															case 'review':
																statusClass = 'bg-danger parpadeo';
																statusText = 'Revisar';
																break;
															case 'approved':
																statusClass = 'bg-success';
																statusText = 'Aprobado';
																break;
															case 'sent':
																statusClass = 'bg-primary';
																statusText = 'Enviado';
																break;
															default:
																statusClass = 'bg-light text-dark';
																statusText = el.status;
														}


														return (
															<React.Fragment key={el.id}>
																<tr className={'has-details ' + (detailsOpened.includes(el.id) ? 'opened' : '')} onClick={() => toggleDetails(el.id)}>
																	<td className="title_supplies-estacion">
																		<div className="d-flex justify-content-between">
																			<span>{el.station?.name}</span>
																		</div>
																	</td>
																	<td className="title_supplies-fecha">{moment(el.datetime).format('DD-MM-YYYY')}</td>
																	<td className="title_supplies-fecha-envio">{el.sent_at ? moment(el.sent_at).format('DD-MM-YYYY / HH:mm') : ''}</td>
																	<td className="title_supplies-pedido">{el.code}</td>
																	<td className="title_supplies-albaran">
																		<div>{el.albaran ?? '-'}</div>
																		<small>{el.albaran_date ? moment(el.albaran_date).format('DD-MM-YYYY') : '-' }</small>
																	</td>
																	<td className="title_supplies-estado">
																		<span className={'badge ' + statusClass}>{statusText}</span>
																	</td>
																	<td className={'title_supplies-chat ' + (!el.pending_chat_messages_count ? 'disabled' : '')}>
																		<button className="btn btn-link btn-sm text-plenoil-brown-light" onClick={(e) => openChat(e, el.station.id)}><i className={'bi bi-chat-right' + (el.pending_chat_messages_count ? '-fill' : '')}></i></button>
																	</td>
																</tr>
																{ detailsOpened.includes(el.id) &&
																	<Details 
																		id={el.id} 
																		forceReload={() => setForceReload(Math.random())}
																	/>
																}
															</React.Fragment>
														)
													}) }

													{ orders.data && !orders.data.length && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

													{ orders.data === undefined && <TrSkeleton rows={skeletonRows} columns={5} /> }
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="col-md-12 text-center mb-3">
				<div className="d-inline-block">
					<Paginator
						min={1}
						current={orders?.current_page}
						max={orders?.last_page}
						changeCallback={(page) => setPage(page)}
					/>
				</div>
			</div>
		</LoggedLayout>
	);
}